// The platform's locale vocabulary. Pure and domain-free: no module type appears here, which is
// exactly why it belongs in core rather than in any one module's _shared.
//
// Separate from `@core/libs/i18n`, which INSTANTIATES i18next. This module creates no singleton, so
// it is safe to re-export from the root `@core` barrel.
import { z } from "zod";

/**
 * The three locales every applicant-facing content string carries. `ar` exists here only because
 * the API still stores `*_ar` content fields; the interface itself ships `fr` and `en` (see
 * `SUPPORTED_LNGS`). Drop `ar` here when the backend drops those fields.
 *
 * The backend spells them as flat `*_fr` / `*_ar` / `*_en` fields; the frontend groups them so a
 * value like a question's text can be passed, copied and validated as a unit.
 *
 * All three are required wherever {@link localizedTextSchema} is used. The platform enforces
 * trilingual parity on its own UI copy, and content that shipped untranslated would render French to
 * an Arabic reader — exactly what that rule exists to prevent.
 */
export const LOCALES = ["fr", "ar", "en"] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * Narrow an i18next language to a platform locale.
 *
 * Takes the base subtag, so a regional tag like `ar-DZ` or `en-GB` still resolves. Falls back to
 * `fr`, the platform's primary and the fallback everywhere else.
 */
export function toLocale(language: string): Locale {
  const base = language.split("-")[0] ?? "fr";
  return LOCALES.find((locale) => locale === base) ?? "fr";
}

/** A string in all three platform locales. */
export interface LocalizedText {
  fr: string;
  ar: string;
  en: string;
}

/** An empty {@link LocalizedText} — the starting point for a new translatable value. */
export function emptyLocalizedText(): LocalizedText {
  return { fr: "", ar: "", en: "" };
}

/**
 * Read the active locale's value out of a record carrying flat `<field>_fr` / `_ar` / `_en` columns —
 * which is how the API sends every translatable string.
 *
 * Falls back to French when the active locale's value is empty, not just when it is missing: a
 * half-translated row should render the primary language rather than a blank cell.
 *
 * Replaces the `locale === "ar" ? x_ar : locale === "en" ? x_en : x_fr` ternary, which was written out
 * once per field at each call site.
 */
export function pickLocalized<F extends string>(
  row: Record<`${F}_${Locale}`, string>,
  field: F,
  locale: Locale
): string {
  return row[`${field}_${locale}`] || row[`${field}_fr`];
}

/** Build a localized-text schema whose per-locale message is the given i18n key. */
export function localizedTextSchema(requiredMessage: string) {
  return z.object({
    fr: z.string().trim().min(1, requiredMessage),
    ar: z.string().trim().min(1, requiredMessage),
    en: z.string().trim().min(1, requiredMessage),
  });
}
